import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AccountSelectionRequest,
  AuthorizationAttemptResponse,
  AuthorizeRequest,
  AuthorizeResponse,
  Capability,
  GatewayProvider,
  InstalledMetaApp,
  IntegrationKey,
  IntegrationStateResponse,
  IntegrationsListResponse,
  TelecmiConfigurationRequest,
  TelecmiConfigurationResponseData
} from './integrations.model';

/**
 * Thin client for the App Service integrations gateway (`/app/app/integrations/*`).
 * Frontend talks only to this gateway — never SMM/CRM's internal integration
 * APIs directly. Response envelope everywhere is `{ data: T }`.
 */
@Injectable({ providedIn: 'root' })
export class IntegrationsApiService {
  // Confirmed against stage (2026-09-24, 20 consecutive 200s): the deployed
  // gateway path is /app/app/integrations, not /app/integrations.
  private readonly base = environment.baseUrl + 'app/app/integrations';

  constructor(private http: HttpClient) {}

  /** GET /app/app/integrations?businessId= */
  list(businessId: string): Observable<IntegrationsListResponse> {
    return this.http.get<IntegrationsListResponse>(`${this.base}?businessId=${businessId}`);
  }

  /** GET /app/app/integrations/state?businessId= — authoritative Meta resources + app grants */
  getState(businessId: string): Observable<IntegrationStateResponse> {
    return this.http.get<IntegrationStateResponse>(`${this.base}/state?businessId=${businessId}`);
  }

  /** GET /app/app/integrations/providers/META/apps?businessId= — installed apps eligible for Meta */
  listProviderApps(provider: GatewayProvider, businessId: string): Observable<{ data: InstalledMetaApp[] }> {
    return this.http.get<{ data: InstalledMetaApp[] }>(`${this.base}/providers/${provider}/apps?businessId=${businessId}`);
  }

  // Deliberately no client for `POST …/resources/{id}/apps/{appId}/provision` or the
  // app-grant `PATCH`: those are server-side lifecycle routes (the backend reconciles
  // installed apps automatically) and must never be offered as UI actions.

  /** DELETE /app/app/integrations/resources/{resourceId}?businessId= — removes every app grant on the resource */
  disconnectResource(resourceId: string, businessId: string): Observable<any> {
    return this.http.delete(`${this.base}/resources/${resourceId}?businessId=${businessId}`);
  }

  /** POST /app/app/integrations/{key}/authorize */
  authorize(key: IntegrationKey, request: AuthorizeRequest): Observable<AuthorizeResponse> {
    // Meta consent is one server-owned bundle, the account type is derived and the
    // installed apps are reconciled server-side: the browser sends only
    // businessId and staffId — never appIds, capabilities or accountType.
    if (key === 'FACEBOOK' || key === 'INSTAGRAM') {
      const { businessId, staffId } = request;
      return this.http.post<AuthorizeResponse>(`${this.base}/${key}/authorize`, { businessId, staffId });
    }
    return this.http.post<AuthorizeResponse>(`${this.base}/${key}/authorize`, request);
  }

  /** GET /app/app/integrations/authorization-attempt/{id}?businessId= */
  getAuthorizationAttempt(attemptId: string, businessId: string): Observable<AuthorizationAttemptResponse> {
    return this.http.get<AuthorizationAttemptResponse>(`${this.base}/authorization-attempt/${attemptId}?businessId=${businessId}`);
  }

  /** POST /app/app/integrations/account-selection?businessId= */
  submitAccountSelection(businessId: string, request: AccountSelectionRequest): Observable<any> {
    return this.http.post(`${this.base}/account-selection?businessId=${businessId}`, request);
  }

  /** PATCH /app/app/integrations/{key}/connections/{connectionId}?businessId=&enabled=&makeDefault= */
  setConnectionEnabled(
    key: IntegrationKey,
    connectionId: string,
    businessId: string,
    enabled: boolean,
    makeDefault?: boolean
  ): Observable<any> {
    let query = `businessId=${businessId}&enabled=${enabled}`;
    if (makeDefault !== undefined) {
      query += `&makeDefault=${makeDefault}`;
    }
    return this.http.patch(`${this.base}/${key}/connections/${connectionId}?${query}`, {});
  }

  /** POST /app/app/integrations/telecmi/configuration */
  saveTelecmiConfiguration(request: TelecmiConfigurationRequest): Observable<{ data: TelecmiConfigurationResponseData }> {
    return this.http.post<{ data: TelecmiConfigurationResponseData }>(`${this.base}/telecmi/configuration`, request);
  }

  /** DELETE /app/app/integrations/telecmi/configuration?businessId= */
  disconnectTelecmi(businessId: string): Observable<any> {
    return this.http.delete(`${this.base}/telecmi/configuration?businessId=${businessId}`);
  }

  // -- Capability editor (Meta/LinkedIn only — CRM Lead Forms/Ads/Inbox/ ----
  // -- Comments have no card of their own, so this is their only UI path). --

  /** PUT /app/app/integrations/connections/{connectionId}/capabilities?businessId=&provider= */
  replaceConnectionCapabilities(
    connectionId: string,
    businessId: string,
    provider: GatewayProvider,
    capabilities: Capability[]
  ): Observable<any> {
    return this.http.put(`${this.base}/connections/${connectionId}/capabilities?businessId=${businessId}&provider=${provider}`, {
      capabilities
    });
  }

  /** DELETE /app/app/integrations/connections/{connectionId}/capabilities?businessId=&provider=&capability= */
  removeConnectionCapability(
    connectionId: string,
    businessId: string,
    provider: GatewayProvider,
    capability: Capability
  ): Observable<any> {
    return this.http.delete(
      `${this.base}/connections/${connectionId}/capabilities?businessId=${businessId}&provider=${provider}&capability=${capability}`
    );
  }

  // -- Disconnect and Disconnect guide §2 — removes the whole connected -----
  // -- account, not just one capability. TeleCMI never uses this (§3). -----

  /** DELETE /app/app/integrations/connections/{connectionId}?businessId=&provider= */
  disconnectConnection(connectionId: string, businessId: string, provider: GatewayProvider): Observable<any> {
    return this.http.delete(`${this.base}/connections/${connectionId}?businessId=${businessId}&provider=${provider}`);
  }
}
